/** Welcome plan-tour state: slot → duration → invite → outro on /app/plan. */
import { useCallback, useMemo, useRef, useState } from 'react';
import {
  collectUnscheduledCandidateIds,
  countNewDumpTasks,
  selectDumpHint,
  selectOnboardingSlotHighlightTaskId,
  selectOutroVariant,
} from '@/features/onboarding/utils/onboardingWelcomePlanHints';
import {
  endOnboardingWelcomePlanTour,
  isOnboardingWelcomePlanTourPending,
} from '@/features/onboarding/utils/onboardingWelcomePlanSession';
import { DEFAULT_TASK_DURATION_MINS } from '@/config/constants';
import { useOnboardingWelcomePlanTourEffects } from '@/features/onboarding/hooks/useOnboardingWelcomePlanTourEffects';

/** @typedef {'invite' | 'slot' | 'duration' | 'outro'} WelcomePlanPhase */
/** @typedef {'firstStep' | 'task'} SlotDragHintKind */

/**
 * @param {Object} params
 * @param {import('@/types').Task[]} params.unscheduledTasks
 * @param {import('@/types').Task[]} params.nextActions
 * @param {Map<string, import('@/types').Task>} [params.tasksMap]
 */
export function useOnboardingWelcomePlanTour({
  unscheduledTasks,
  nextActions,
  tasksMap = new Map(),
}) {
  const [active, setActive] = useState(() =>
    isOnboardingWelcomePlanTourPending(),
  );
  const [phase, setPhase] = useState/** @type {WelcomePlanPhase} */ ('slot');
  const [outroVariant, setOutroVariant] = useState/** @type {import('@/features/onboarding/utils/onboardingWelcomePlanHints').OutroVariant} */ (
    'unplanned',
  );
  const baselineIdsRef = useRef(new Set());
  const [addTaskHighlightUsed, setAddTaskHighlightUsed] = useState(false);
  const seenSlotHighlightRef = useRef(/** @type {string | null} */ (null));
  const slotTargetTaskIdRef = useRef(/** @type {string | null} */ (null));
  const slotSkippedRef = useRef(false);
  const durationBaselineRef = useRef(
    /** @type {number | null} */ (null),
  );
  const [durationHighlightTaskId, setDurationHighlightTaskId] = useState(
    /** @type {string | null} */ (null),
  );

  const slotHighlightTaskId = useMemo(() => {
    if (!active || phase !== 'slot') {
      return null;
    }
    return selectOnboardingSlotHighlightTaskId({
      unscheduledTasks,
      nextActions,
      tasksMap,
    });
  }, [active, phase, unscheduledTasks, nextActions, tasksMap]);

  /** @type {SlotDragHintKind} */
  const slotDragHintKind = useMemo(() => {
    if (!slotHighlightTaskId) {
      return 'task';
    }
    const highlighted =
      (unscheduledTasks ?? []).find((task) => task.id === slotHighlightTaskId) ??
      (nextActions ?? []).find((task) => task.id === slotHighlightTaskId);
    return highlighted?.parentId ? 'firstStep' : 'task';
  }, [slotHighlightTaskId, unscheduledTasks, nextActions]);

  const resolveTaskById = useCallback(
    (id) => {
      if (!id) {
        return null;
      }
      return (
        tasksMap.get(id) ??
        (unscheduledTasks ?? []).find((task) => task.id === id) ??
        (nextActions ?? []).find((task) => task.id === id) ??
        null
      );
    },
    [tasksMap, unscheduledTasks, nextActions],
  );

  const enterInvite = useCallback(() => {
    if (!slotTargetTaskIdRef.current) {
      const highlightId = selectOnboardingSlotHighlightTaskId({
        unscheduledTasks,
        nextActions,
        tasksMap,
      });
      if (highlightId) {
        slotTargetTaskIdRef.current = highlightId;
      }
    }
    baselineIdsRef.current = collectUnscheduledCandidateIds(unscheduledTasks);
    setAddTaskHighlightUsed(false);
    setDurationHighlightTaskId(null);
    durationBaselineRef.current = null;
    setPhase('invite');
  }, [unscheduledTasks, nextActions, tasksMap]);

  const enterDuration = useCallback(() => {
    const targetId = slotTargetTaskIdRef.current;
    const task = resolveTaskById(targetId);
    durationBaselineRef.current =
      task?.duration ?? DEFAULT_TASK_DURATION_MINS;
    setDurationHighlightTaskId(targetId);
    setPhase('duration');
  }, [resolveTaskById]);

  const newDumpCount = useMemo(() => {
    if (!active || phase !== 'invite') {
      return 0;
    }
    return countNewDumpTasks(unscheduledTasks, baselineIdsRef.current);
  }, [active, phase, unscheduledTasks]);

  const dumpHint = useMemo(
    () => (phase === 'invite' ? selectDumpHint(newDumpCount) : null),
    [phase, newDumpCount],
  );

  const highlightAddTask =
    active && phase === 'invite' && !addTaskHighlightUsed;

  const finishTour = useCallback(() => {
    endOnboardingWelcomePlanTour();
    setActive(false);
    setPhase('slot');
    seenSlotHighlightRef.current = null;
    slotTargetTaskIdRef.current = null;
    slotSkippedRef.current = false;
    durationBaselineRef.current = null;
    setDurationHighlightTaskId(null);
    setAddTaskHighlightUsed(false);
  }, []);

  const goToOutro = useCallback((variant) => {
    setOutroVariant(variant);
    setPhase('outro');
  }, []);

  const finishMoreToOutro = useCallback(() => {
    goToOutro(
      selectOutroVariant({
        slotTargetTaskId: slotTargetTaskIdRef.current,
        unscheduledTasks,
        baselineIds: baselineIdsRef.current,
        tasksMap,
        nextActions,
      }),
    );
  }, [unscheduledTasks, tasksMap, nextActions, goToOutro]);

  const onAddTaskHighlightUsed = useCallback(() => {
    setAddTaskHighlightUsed(true);
  }, []);

  const skipInvite = useCallback(() => {
    finishMoreToOutro();
  }, [finishMoreToOutro]);

  const completeSlot = useCallback(() => {
    slotSkippedRef.current = true;
    enterInvite();
  }, [enterInvite]);

  const completeDuration = useCallback(() => {
    enterInvite();
  }, [enterInvite]);

  const closeOutro = useCallback(() => {
    finishTour();
  }, [finishTour]);

  useOnboardingWelcomePlanTourEffects({
    active,
    setActive,
    phase,
    setPhase,
    seenSlotHighlightRef,
    slotTargetTaskIdRef,
    slotSkippedRef,
    durationBaselineRef,
    setAddTaskHighlightUsed,
    slotHighlightTaskId,
    unscheduledTasks,
    enterInvite,
    enterDuration,
    resolveTaskById,
    tasksMap,
    nextActions,
  });

  const markNextActionPanelSeenInSlot =
    phase === 'slot' && slotDragHintKind === 'firstStep';

  return {
    active,
    phase,
    outroVariant,
    newDumpCount,
    dumpHint,
    highlightAddTask,
    markNextActionPanelSeenInSlot,
    slotHighlightTaskId,
    slotDragHintKind,
    highlightTimeSlot: active && phase === 'slot',
    durationHighlightTaskId,
    onAddTaskHighlightUsed,
    skipInvite,
    completeSlot,
    completeDuration,
    closeOutro,
    finishTour,
  };
}
